import React, { useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  Stack,
  Typography
} from "@mui/material";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { extractFile } from "../../../contexts/services/ai-assistant/aiAssistantServices";
import { ClassifyProcessGuideBanner } from "./ClassifyProcessGuide";

const MAX_FILE_SIZE = 5 * 1024 * 1024;
const ACCEPTED_EXTENSIONS = [
  "xlsx",
  "xls",
  "pdf",
  "jpeg",
  "jpg",
  "png",
  "csv",
  "txt"
];
const FILE_ACCEPT = ACCEPTED_EXTENSIONS.map(
  (extension) => `.${extension}`
).join(",");

const formatFileSize = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
};

const validateFile = (file) => {
  if (!file) return "Please select a file.";
  const extension = file.name.split(".").pop()?.toLowerCase();
  if (!ACCEPTED_EXTENSIONS.includes(extension)) {
    return "Unsupported file type. Select an Excel, PDF, image, CSV, or TXT file.";
  }
  if (file.size === 0) return "The selected file is empty.";
  if (file.size > MAX_FILE_SIZE) return "The file must be 5 MB or smaller.";
  return "";
};

const UploadDocumentStep = ({
  onExtracted,
  onContinue,
  extractionResult,
  resetSignal,
  extract = extractFile,
  guideProps,
  title = "Upload your document",
  description = "Upload a document to extract its data and classify each row for processing."
}) => {
  const inputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [error, setError] = useState("");
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  const selectFile = (nextFile) => {
    const validationError = validateFile(nextFile);
    setError(validationError);
    setFile(validationError ? null : nextFile);
    onExtracted(null);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setIsDragging(false);
    selectFile(event.dataTransfer.files?.[0]);
  };

  const removeFile = () => {
    setFile(null);
    setError("");
    onExtracted(null);
    if (inputRef.current) inputRef.current.value = "";
  };

  useEffect(() => {
    setFile(null);
    setError("");
    setIsDragging(false);
    setIsUploading(false);
    if (inputRef.current) inputRef.current.value = "";
  }, [resetSignal]);

  const uploadFile = async () => {
    const validationError = validateFile(file);
    if (validationError) {
      setError(validationError);
      return;
    }

    setIsUploading(true);
    setError("");
    try {
      const result = await extract(file);
      onExtracted(result);
    } catch (uploadError) {
      setError(
        uploadError?.message ||
          "The file could not be extracted. Please try again."
      );
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <Box sx={{ p: 2 }} mx='auto'>
      <Typography variant='h5' fontWeight={700} gutterBottom>
        {title}
      </Typography>
      {guideProps && <ClassifyProcessGuideBanner {...guideProps} />}

      <Typography color='text.secondary' sx={{ mb: 3, textAlign: "justify" }}>
        {description}
      </Typography>

      <Paper
        variant='outlined'
        onDragEnter={(event) => {
          event.preventDefault();
          setIsDragging(true);
        }}
        onDragOver={(event) => event.preventDefault()}
        onDragLeave={() => setIsDragging(false)}
        onDrop={handleDrop}
        sx={{
          p: 4,
          textAlign: "center",
          borderStyle: "dashed",
          borderWidth: 2,
          borderColor: isDragging ? "primary.main" : "divider",
          bgcolor: isDragging ? "action.hover" : "background.paper",
          transition: "all 0.2s ease"
        }}
      >
        <CloudUploadOutlinedIcon color='primary' sx={{ fontSize: 52, mb: 1 }} />
        <Typography variant='h6' fontWeight={600}>
          Drag and drop a file here
        </Typography>
        <Typography variant='body2' color='text.secondary' sx={{ my: 1.5 }}>
          or
        </Typography>
        <Button variant='outlined' onClick={() => inputRef.current?.click()}>
          Browse files
        </Button>
        <input
          ref={inputRef}
          type='file'
          aria-label='Document file'
          accept={FILE_ACCEPT}
          hidden
          onChange={(event) => selectFile(event.target.files?.[0])}
        />
        <Typography
          variant='caption'
          display='block'
          color='text.secondary'
          sx={{ mt: 2 }}
        >
          Excel, PDF, JPEG/PNG, CSV, or TXT · Maximum 5 MB
        </Typography>
      </Paper>

      {file && (
        <Paper variant='outlined' sx={{ p: 2, mt: 2 }}>
          <Stack direction='row' alignItems='center' spacing={1.5}>
            <DescriptionOutlinedIcon color='primary' />
            <Box flex={1} minWidth={0}>
              <Typography fontWeight={600} noWrap>
                {file.name}
              </Typography>
              <Typography variant='caption' color='text.secondary'>
                {formatFileSize(file.size)}
              </Typography>
            </Box>
            <Button
              size='small'
              startIcon={<DeleteOutlineIcon />}
              onClick={removeFile}
              disabled={isUploading}
              sx={{
                textTransform: "none",
                color: "error.main",
                "&:hover": { backgroundColor: "error.hover" }
              }}
            >
              Remove
            </Button>
          </Stack>
        </Paper>
      )}

      {error && (
        <Alert severity='error' sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}
      {extractionResult && (
        <Alert severity='success' sx={{ mt: 2 }}>
          File extracted successfully. It is ready for column selection.
        </Alert>
      )}

      <Stack spacing={1.5} sx={{ mt: 2, mb: 1 }}>
        <Button
          variant='contained'
          size='large'
          onClick={uploadFile}
          disabled={!file || isUploading}
          startIcon={
            isUploading ? <CircularProgress size={18} color='inherit' /> : null
          }
          sx={{ textTransform: "none", width: "100%" }}
        >
          {isUploading ? "Extracting..." : "Upload and extract"}
        </Button>
        {extractionResult && onContinue && (
          <Button
            variant='outlined'
            size='large'
            onClick={onContinue}
            disabled={isUploading}
            sx={{ textTransform: "none", width: "100%" }}
          >
            Continue to column selection
          </Button>
        )}
      </Stack>
    </Box>
  );
};

export { ACCEPTED_EXTENSIONS, MAX_FILE_SIZE, validateFile };
export default UploadDocumentStep;
